import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { SearchField } from './search-field.interface';

/**
 * Componente genérico de búsqueda
 * Construye dinámicamente un formulario de búsqueda dentro de un modal Bootstrap
 * basándose en una configuración de campos (SearchField[])
 * 
 * @example
 * <app-search-component 
 *   [formGroup]="miFormulario"
 *   [fields]="miConfiguracionCampos"
 *   (onSearch)="buscar()">
 * </app-search-component>
 */
@Component({
  selector: 'app-search-component',
  templateUrl: './search-component.component.html',
  styleUrls: ['./search-component.component.scss']
})
export class SearchComponentComponent {
  /** Título del modal (clave de traducción) */
  @Input() title: string = '$general.txtSearch';
  
  /** Texto del botón de búsqueda (clave de traducción) */
  @Input() searchButtonText: string = '$general.btnSearch';
  
  /** Texto del botón de reset (clave de traducción) */
  @Input() resetButtonText: string = '$general.btnReset';
  
  /** Si true, muestra el contenido en un modal. Si false, lo muestra directamente */
  @Input() showModal: boolean = true;
  
  /** ID del modal Bootstrap */
  @Input() modalId: string = 'searchModal';
  
  /** Tamaño del modal: 'sm', 'lg' o 'xl' */
  @Input() modalSize: 'sm' | 'lg' | 'xl' = 'xl';
  
  /** Configuración de campos a mostrar (define tipo, etiqueta, opciones, etc.) */
  @Input() fields: SearchField[] = [];
  
  /** FormGroup del componente padre que contiene los FormControls */
  @Input() formGroup: FormGroup;
  
  /** Evento emitido cuando se hace clic en el botón de búsqueda */
  @Output() onSearch = new EventEmitter<void>();
  
  /** Evento emitido cuando se hace clic en el botón de reset */
  @Output() onReset = new EventEmitter<void>();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
  ) {}

  /**
   * Emite el evento de búsqueda
   */
  public search(): void {
    this.onSearch.emit();
  }

  /**
   * Emite el evento de reset
   */
  public reset(): void {
    this.onReset.emit();
  }

  /**
   * Determina si un campo debe estar deshabilitado
   * Soporta tanto valores booleanos como funciones que retornan boolean
   * @param field - El campo a evaluar
   * @returns true si el campo está deshabilitado, false en caso contrario
   */
  public isFieldDisabled(field: SearchField): boolean {
    if (typeof field.disabled === 'function') {
      return field.disabled();
    }
    return field.disabled || false;
  }
}
